import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import TablePagination from "./TablePagination";
import { selectStyles } from "../helper/SelectStyles";
import { getProducts, deleteProduct } from "../Apis/ProductAPIs";
import { getCategories } from "../Apis/CategoryAPIs";
import usePermissions from "../hook/userPermission";
import { toast } from "react-toastify";

const ProductListLayer = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [filterSegment, setFilterSegment] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const segmentOptions = [
    { value: "B2B", label: "B2B" },
    { value: "B2C", label: "B2C" },
    { value: "Both", label: "Both" },
  ];

  const fetchCategoriesOnce = async () => {
    const catRes = await getCategories();
    if (catRes.status) {
      const cData = catRes.response?.data || catRes.response;
      setCategories(Array.isArray(cData) ? cData : []);
    }
  };

  const fetchFilteredProducts = async () => {
    setLoading(true);
    const params = {
      page: currentPage,
      limit: rowsPerPage
    };
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;
    if (filterSegment) params.segment = filterSegment;
    if (filterCategory) params.categoryId = filterCategory;
    if (filterStatus !== "") params.isActive = filterStatus;

    const prodRes = await getProducts(params);
    if (prodRes.status) {
      const pData = prodRes.response?.data || prodRes.response;
      setProducts(Array.isArray(pData) ? pData : []);
      setTotalRecords(prodRes.response?.total || (Array.isArray(pData) ? pData.length : 0));
    }
    setLoading(false);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterCategory("");
    setFilterSegment("");
    setFilterStatus("");
    setCurrentPage(0);
  };

  useEffect(() => {
    fetchCategoriesOnce();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchFilteredProducts();
  }, [debouncedSearchTerm, filterSegment, filterCategory, filterStatus, currentPage, rowsPerPage]);

  const categoryOptions = categories.map(cat => ({ value: cat._id || cat.id, label: cat.name }));

  const filterSegmentOptions = [
    { value: "", label: "All Segments" },
    ...segmentOptions
  ];

  const filterCategoryOptions = [
    { value: "", label: "All Exhibition Types" },
    ...categoryOptions
  ];

  const filterStatusOptions = [
    { value: "", label: "All Status" },
    ...statusOptions
  ];

  const currentProducts = products;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const confirmDelete = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!productToDelete) return;
    setIsUpdating(true);
    const res = await deleteProduct(productToDelete._id || productToDelete.id);
    if (res.status) {
      toast.success(`Product ${(productToDelete?.isActive ?? productToDelete?.status) ? "deleted" : "enabled"} successfully`);
      fetchFilteredProducts();
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
    setIsUpdating(false);
  };

  const handleCloseDelete = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom py-10 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3" style={{ background: "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)" }}>
        <h6 className="text-white pb-2 mb-0">Products</h6>
        {hasPermission("products", "add") && (
          <Link
            to="/products/add"
            className="btn text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
          >
            <Icon icon="ic:baseline-plus" className="text-xl" />
            Add Product
          </Link>
        )}
      </div>
      <div className="card-body p-24">

        {/* Filter Bar */}
        <div className="mb-24 p-16 radius-8 bg-neutral-50 border">
          <div className="row gy-3">
            <div className="col-md-3">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">Search Products</label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control radius-8 ps-40"
                  style={{ height: '44px' }}
                  placeholder="Search Products..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
                />
                <Icon icon="ion:search-outline" className="position-absolute start-12 top-50 translate-middle-y text-secondary-light" style={{ left: '14px' }} />
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">Exhibition Type</label>
              <Select
                options={filterCategoryOptions}
                isSearchable={false}
                value={filterCategoryOptions.find(opt => opt.value === filterCategory)}
                onChange={(val) => { setFilterCategory(val ? val.value : ""); setCurrentPage(0); }}
                styles={selectStyles()}
                placeholder="All Exhibition Types"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">Segment</label>
              <Select
                options={filterSegmentOptions}
                isSearchable={false}
                value={filterSegmentOptions.find(opt => opt.value === filterSegment)}
                onChange={(val) => { setFilterSegment(val ? val.value : ""); setCurrentPage(0); }}
                styles={selectStyles()}
                placeholder="All Segments"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase p-0 m-0 mb-1">Status</label>
              <Select
                options={filterStatusOptions}
                isSearchable={false}
                value={filterStatusOptions.find(opt => opt.value === filterStatus)}
                onChange={(val) => { setFilterStatus(val ? val.value : ""); setCurrentPage(0); }}
                styles={selectStyles()}
                placeholder="All Status"
              />
            </div>
             <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-danger h-44-px w-100 radius-8 fw-bold d-flex align-items-center justify-content-center gap-2"
                onClick={clearFilters}
                title="Clear Filters"
              >
                <Icon icon="solar:restart-bold" className="text-xl" />
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr style={{ backgroundColor: "#f7e7da" }}>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>S.No</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Product Name</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Exhibition Type</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Segment</th>
                <th scope="col" className="text-center" style={{ color: "black", fontWeight: "bold" }}>Status</th>
                <th scope="col" className="text-center" style={{ color: "black", fontWeight: "bold" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
              ) : currentProducts.length > 0 ? (
                currentProducts.map((product, index) => (
                  <tr key={product._id || product.id}>
                    <td>{currentPage * rowsPerPage + index + 1}</td>
                    <td>{product.productName}</td>
                    <td>{product.categoryName || "N/A"}</td>
                    <td>
                      <span className={`badge ${product.segment === 'B2B' ? 'bg-primary-50 text-primary-600' : product.segment === 'B2C' ? 'bg-info-50 text-info-600' : 'bg-warning-50 text-warning-600'} px-20 py-4 radius-4`}>
                        {product.segment || "N/A"}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${(product.isActive ?? product.status) ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'} px-20 py-4 radius-4`}>
                        {(product.isActive ?? product.status) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("products", "edit") && (
                          <button
                            onClick={() => navigate(`/products/edit/${product._id || product.id}`)}
                            type="button"
                            className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {hasPermission("products", "delete") && (
                          <button
                            type="button"
                            onClick={() => confirmDelete(product)}
                            title="Delete"
                            className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                          >
                            <Icon icon="lucide:trash-2" className="text-xl" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalRecords={totalRecords}
        />
      </div>

      {/* Delete/Status Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDelete} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className={`${(productToDelete?.isActive ?? productToDelete?.status) ? "bg-success-focus" : "bg-danger-focus"} rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px`}>
              <Icon icon={(productToDelete?.isActive ?? productToDelete?.status) ? "lucide:unlock" : "lucide:lock"} className="text-xxl" />
            </div>
          </div>
          <h5 className="mb-3">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to {(productToDelete?.isActive ?? productToDelete?.status) ? "disable" : "enable"} this product?
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32"
              onClick={handleCloseDelete}
            >
              Cancel
            </Button>
            <Button
              variant={(productToDelete?.isActive ?? productToDelete?.status) ? "danger" : "success"}
              className="px-32"
              onClick={handleDelete}
              disabled={isUpdating}
            >
              {isUpdating ? "Processing..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProductListLayer;
